import React, { useState } from "react";
import Button from "./Button";

interface RegistrationFormProps {
  onSubmit: (data: {
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string;
    employerName: string;
    marketingConsent: boolean;
  }) => void;
  isLoading?: boolean;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  onSubmit,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    jobTitle: "",
    employerName: "",
    marketingConsent: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = "Job title is required";
    }
    if (!formData.employerName.trim()) {
      newErrors.employerName = "Employer name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm() && !isLoading) {
      onSubmit(formData);
    }
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">
        Regístrate para Comenzar
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Nombre
            </label>
            <input
              type="text"
              id="firstName"
              disabled={isLoading}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.firstName ? "border-red-500" : "border-gray-300"
              } focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed`}
              value={formData.firstName}
              onChange={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Apellido
            </label>
            <input
              type="text"
              id="lastName"
              disabled={isLoading}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.lastName ? "border-red-500" : "border-gray-300"
              } focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed`}
              value={formData.lastName}
              onChange={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Correo Electrónico Personal
          </label>
          <input
            type="email"
            id="email"
            disabled={isLoading}
            className={`w-full px-3 py-2 border rounded-md ${
              errors.email ? "border-red-500" : "border-gray-300"
            } focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed`}
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-500">{errors.email}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="jobTitle"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Título Profesional
          </label>
          <input
            type="text"
            id="jobTitle"
            disabled={isLoading}
            className={`w-full px-3 py-2 border rounded-md ${
              errors.jobTitle ? "border-red-500" : "border-gray-300"
            } focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed`}
            value={formData.jobTitle}
            onChange={(e) =>
              setFormData({ ...formData, jobTitle: e.target.value })
            }
          />
          {errors.jobTitle && (
            <p className="mt-1 text-sm text-red-500">{errors.jobTitle}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="employerName"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Nombre de Compañía o Patrono
          </label>
          <input
            type="text"
            id="employerName"
            disabled={isLoading}
            className={`w-full px-3 py-2 border rounded-md ${
              errors.employerName ? "border-red-500" : "border-gray-300"
            } focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed`}
            value={formData.employerName}
            onChange={(e) =>
              setFormData({ ...formData, employerName: e.target.value })
            }
          />
          {errors.employerName && (
            <p className="mt-1 text-sm text-red-500">{errors.employerName}</p>
          )}
        </div>

        <div className="flex items-start space-x-3">
            <label
              className="text-sm text-gray-600"
            >
              Al continuar, aceptas recibir correos electrónicos útiles de YOJ Inc. sobre tus resultados, recursos gratuitos y actualizaciones ocasionales. Puedes darte de baja en cualquier momento. Consulta nuestra Política de Privacidad y nuestros Términos de Uso.
            </label>
          </div>

        <Button type="submit" fullWidth disabled={isLoading}>
          {isLoading ? "Procesando..." : "Obtén tus resultados ahora"}
        </Button>
      </form>
    </div>
  );
};

export default RegistrationForm;
